import {
  Button,
  Chip,
  Search,
  Text,
  TextLink,
  VSpacer,
} from '@/components/DesignSystem';
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { formatDate, formatTime } from '@shared/utilities';
import i18next from 'i18next';
import { QueryKeys } from '../../../../constants/QueryKeys';
import { NotificationDetailModal } from '../modals/NotificationDetailModal';
import { NotificationRecipientsModal } from '../modals/NotificationRecipientsModal';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { ScheduledNotificationApi } from "@/utilities/api/ScheduledNotificationApi";
import { ApiScheduledNotification } from "@api/interfaces";
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
  ScheduledNotificationStatus,
} from '@shared/enums/ScheduledNotificationStatus';
import { DeepLinkUrls } from "@shared/enums/ScheduledNotificationType";
import { FilterOptions } from '@shared/interfaces/ScheduledNotification';
import { ScheduledNotificationEndpoint } from '@api/endpoints/ScheduledNotificationEndpoint';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useTranslation } from 'react-i18next';
import { useNotificationStatusLabel } from '../utils/useNotificationStatusLabel';
import { Routes } from "@/constants/Routes";
import { useNavigate } from "react-router-dom";

export const NotificationDraftTable: React.FC = () => {
  const { t } = useTranslation();
  const statusLabel = useNotificationStatusLabel();
  const { isInternal } = useAuthentication();
  const navigate = useNavigate();
  const scheduledNotificationRoute = isInternal
    ? Routes.ADMIN_SCHEDULED_NOTIFICATION
    : Routes.SCHEDULED_NOTIFICATION;

  type UINotification = ApiScheduledNotification & {
    retailerName: string;
  };

  const [clickOnDelete, setClickOnDelete] = useState(false);
  const [notificationToDelete, setNotificationToDelete] =
    useState<ApiScheduledNotification | null>(null);
  const [sortBy, setSortBy] =
    useState<ScheduledNotificationEndpoint.List.ScheduledNotificationSort>('sendDate');
  const [sortDesc, setSortDesc] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [selectedNotification, setSelectedNotification] = useState<UINotification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);

  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.GET_DRAFT_NOTIFICATIONS, searchInput, sortBy, sortDesc],
    async ({ pageParam = 0 }) =>
      ScheduledNotificationApi.list({
        page: pageParam,
        limit: 20,
        search: searchInput || undefined,
        sort: sortBy,
        status: ScheduledNotificationStatus.Draft,
        sortDesc,
      }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 60 * 1000,
    },
  );

  const { mutateAsync: deleteNotification } = useMutation(
    async (notification: ApiScheduledNotification) => {
      return ScheduledNotificationApi.delete(notification.id);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_DRAFT_NOTIFICATIONS);
        openSnackbar(t($ => $.notificationBuilder.table.success.deleted));
      },
      onError: () => {
        openSnackbar(t($ => $.notificationBuilder.table.errors.deleteFailed));
      },
    },
  );

  const handleSort = (column: typeof sortBy) => {
    const isSortCurrentlySelected = column === sortBy;
    if (isSortCurrentlySelected) {
      if (column === 'createdAt') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy('createdAt');
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(column);
      setSortDesc(true);
    }
  };

  const notificationCount = data?.pages?.[0]?.total ?? 0;

  const renderTableBody = () => {

    if (!data || data.pages.every(page => page.data.length === 0)) {
      return (
        <TableRow>
          <TableCell align="center" colSpan={6}>
            {searchInput ? (
              <Text>
                {t($ => $.notificationBuilder.table.emptyState.noResults, { searchInput })}
              </Text>
            ) : (
              <Text>
                {t($ => $.notificationBuilder.table.emptyState.noNotifications)}
              </Text>
            )}
          </TableCell>
        </TableRow>
      );
    }

    return data.pages.flatMap(page => page.data.map(n => {
      // Add retailerName if it doesn't exist
      const notification = {
        ...n,
        retailerName: n.retailer?.name ?? '—',
      };
      const recipientCount = notification.recipientCount ?? 0;

      return (
        <TableRow key={notification.id}>
          <TableCell>
            <Chip
              color={
                'info'
              }
              label={
                <Text category="label-small" style={{ textTransform: 'uppercase' }}>
                  {statusLabel[notification.status]}
                </Text>
              }
              testID={`status-chip-${notification.id}`}
              variant="filled"
            />
          </TableCell>
          <TableCell>
            {notification.sendDate
              ? (
                `${
                  formatDate(notification.sendDate, i18next)
                }, ${
                  formatTime(notification.sendDate, i18next)
                }`
              )
              : '—'}
          </TableCell>
          <TableCell>
            <TextLink
              onClick={() => {
                if (recipientCount > 0) {
                  navigate(
                    scheduledNotificationRoute.replace(':id', notification.id),
                  );
                }
              }}
              testID="recipients-link"
            >
              {t(
                $ => $.notificationBuilder.table.recipientCount,
                { count: recipientCount },
              )}
            </TextLink>
          </TableCell>
          {isInternal && (
            <TableCell>
              {notification.retailerName === '—' ? 'none' : notification.retailerName}
            </TableCell>
          )}
          <TableCell>
            <TextLink
              onClick={() => {
                setSelectedNotification(notification);
                setShowDetailModal(true);
              }}
              testID="title-link"
            >
              {notification.title ?? 'none'}
            </TextLink>

          </TableCell>
          <TableCell>

            <Button
              color="error"
              onClick={() => {
                setNotificationToDelete(notification);
                setClickOnDelete(true);
              }}
              size="small"
              square
              startIcon={<CancelScheduleSendIcon />}
              testID="cancel-button"
            >
              {t($ => $.common.actions.delete)}
            </Button>
          </TableCell>
        </TableRow>
      );
    }));
  };

  return (
    <>
      <Stack alignItems="center" direction="row" spacing={6}>
        <Search
          fullWidth={false}
          onChangeText={setSearchInput}
          testID="notification-search"
          value={searchInput}
          width={440}
        />
        <VSpacer size="6" />
      </Stack>
      <VSpacer size="6" />

      <Stack alignItems="center" direction="row" spacing={6}>
        <Text category="body-medium">
          {t(
            $ => $.notificationBuilder.table.customNotificationCount,
            { count: notificationCount },
          )}
        </Text>
      </Stack>
      <VSpacer size="6" />

      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height="42vh"
        isLoading={isFetchingNextPage}
      >
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead
            sx={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
            <TableRow>
              <TableCell width={180}>
                {t($ => $.notificationBuilder.table.headers.status)}
              </TableCell>
              <TableCell width={200}>
                <TableSortLabel
                  active={sortBy === 'sendDate'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('sendDate')}
                >
                  {t($ => $.notificationBuilder.table.headers.sendDate)}
                </TableSortLabel>
              </TableCell>
              <TableCell width={160}>
                {t($ => $.notificationBuilder.table.headers.recipients)}
              </TableCell>
              {isInternal && (
                <TableCell width={180}>
                  <TableSortLabel
                    active={sortBy === 'retailerName'}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort('retailerName')}
                  >
                    {t($ => $.notificationBuilder.table.headers.retailer)}
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell width={280}>
                <TableSortLabel
                  active={sortBy === 'title'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  {t($ => $.notificationBuilder.table.headers.title)}
                </TableSortLabel>
              </TableCell>
              <TableCell width={160}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </InfiniteScroll>
      {clickOnDelete && notificationToDelete && (
        <ConfirmDialog
          isLoading={isLoading}
          onCancel={() => {
            setClickOnDelete(false);
            setNotificationToDelete(null);
          }}
          onConfirm={async () => {
            await deleteNotification(notificationToDelete);
            setClickOnDelete(false);
            setNotificationToDelete(null);
          }}
          open={clickOnDelete}
          testID="confirm-dialog"
          title={t($ => $.notificationBuilder.table.confirm.delete)}
        />
      )}

      {selectedNotification && showDetailModal && (
        <NotificationDetailModal
          communicationType={selectedNotification.communicationType}
          csvRecipientData={selectedNotification.csvRecipientData || []}
          deepLink={(selectedNotification.deepLink as DeepLinkUrls) || null}
          message={selectedNotification.message || ''}
          mode="edit"
          notificationId={selectedNotification.id}
          onClose={() => {
            setSelectedNotification(null);
            setShowDetailModal(false);
            void queryClient.invalidateQueries([QueryKeys.GET_DRAFT_NOTIFICATIONS]);
          }}
          open={showDetailModal}
          options={(selectedNotification.options || {}) as FilterOptions}
          recipientCount={selectedNotification.recipientCount}
          retailerId={selectedNotification.retailerId}
          retailerName={selectedNotification.retailerName}
          sendDate={
            selectedNotification.sendDate
              ? new Date(selectedNotification.sendDate)
              : undefined
          }
          status={selectedNotification.status}
          testID="notification-detail-modal"
          title={selectedNotification.title}
        />
      )}

      {showRecipientsModal && selectedNotification && (
        <NotificationRecipientsModal
          csvRecipientsData={selectedNotification.csvRecipientData || []}
          onClose={() => setShowRecipientsModal(false)}
          options={(selectedNotification.options || {}) as FilterOptions}
          retailerId={selectedNotification.retailerId}
          scheduledNotificationId={selectedNotification.id}
        />
      )}
    </>
  );
};
